import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import PatronGiftCard from './PatronGiftCard';
import api from '../services/api';
import brandConversionService from '../services/brandConversionService';
import useMediaQuery from '../utils/useMediaQuery';
import NotificationIcon from './notifications/NotificationIcon';
import { LockIcon } from './messages/SecureLockSheet';
import { decorate, fillFor, T, RADIUS, QUOTE_FONT, BUBBLE_FONT } from './notifications/notificationModel';

const DROP_SIZE = 7;

/**
 * The bell dropdown (handoff §2): a 420px panel inside the ring-gradient border with the
 * seven latest rows, ALL / UNREAD tabs, MARK ALL READ and a VIEW ALL pill. Rows keep the
 * body's voice in one line — serif quotes, Arial bubbles, mono facts.
 */
export default function NotificationDropdown({ onClose }) {
  const navigate = useNavigate();
  const phone = useMediaQuery('(max-width: 639px)');
  const { notifications, unreadCount, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [giftCard, setGiftCard] = useState(null); // { brandName, brandLogoUrl, message, giftAmountCents, promoLink, promoCode }

  // Fetch when the dropdown opens and when the tab flips.
  useEffect(() => {
    fetchNotifications(1, DROP_SIZE, { unread: unreadOnly });
  }, [fetchNotifications, unreadOnly]);

  const now = useMemo(() => new Date(), [notifications]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(
    () => notifications.filter((n) => !unreadOnly || !n.isRead).slice(0, DROP_SIZE).map((n) => decorate(n, now)),
    [notifications, unreadOnly, now],
  );

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id, notification.family);
    }

    // PartyPatronGiftReceived → open the PatronGiftCard modal instead of navigating away.
    if (notification.type === 'PartyPatronGiftReceived' && notification.relatedEntityId) {
      try {
        const res = await api.get(`/partypatrons/${notification.relatedEntityId}`);
        const patron = res?.data?.data;
        if (patron) {
          // Log notification_opened — fire-and-forget; not critical to the UI flow.
          brandConversionService.logNotificationOpened(patron.brandId, {
            partyPatronId: patron.id,
          }).catch(() => {});

          setGiftCard({
            brandId: patron.brandId,
            partyPatronId: patron.id,
            brandName: patron.brandName,
            brandLogoUrl: patron.brandLogoUrl || notification.imageUrl || null,
            message: patron.customMessage,
            giftAmountCents: patron.guestGiftCents,
            promoLink: patron.promoLink,
            promoCode: patron.promoCode,
          });
          // Don't close the dropdown — modal renders on top.
          return;
        }
      } catch {
        // fall through to default nav
      }
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    onClose();
  };

  const closeGiftCard = () => setGiftCard(null);

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const handleViewAll = (e) => {
    e.preventDefault();
    onClose();
    navigate('/notifications');
  };

  const tab = (label, on, onClick) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{ height: 32, padding: '0 14px', border: '2px solid transparent', borderRadius: RADIUS.pill, background: on ? fillFor('on') : T.off, color: on ? '#fff' : T.ink, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
    >
      {label}
    </button>
  );

  const previewStyle = (n) => ({
    fontFamily: n.variant === 'quote' ? QUOTE_FONT : n.variant === 'bubble' ? BUBBLE_FONT : 'inherit',
    fontStyle: n.variant === 'quote' ? 'italic' : 'normal',
    color: n.variant === 'quote' || n.variant === 'bubble' ? T.ink : T.grey500,
  });

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      data-testid="notification-dropdown"
      className="absolute top-full z-50"
      style={{
        right: phone ? -12 : 0, marginTop: -2, width: 420, maxWidth: 'calc(100vw - 24px)', boxSizing: 'border-box',
        border: '2px solid transparent', borderRadius: RADIUS.panel, background: fillFor('open'), overflow: 'hidden',
        fontFamily: "'JetBrains Mono', Menlo, monospace", color: T.ink, textAlign: 'left',
      }}
    >
      <style>{DROP_CSS}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 16px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '.1em' }}>NOTIFICATIONS</span>
          {unreadCount > 0 && (
            <button type="button" onClick={handleMarkAllRead} className="pr-drop-markall" style={{ border: 0, background: 'none', padding: 0, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: T.grey500, cursor: 'pointer', transition: 'color .2s' }}>
              MARK ALL READ
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab('ALL', !unreadOnly, () => setUnreadOnly(false))}
          {tab(`UNREAD · ${unreadCount}`, unreadOnly, () => setUnreadOnly(true))}
        </div>
      </div>

      <div className="pr-noscroll" style={{ maxHeight: 440, overflowY: 'auto', scrollbarWidth: 'none', padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isLoading && rows.length === 0 ? (
          <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: T.grey500 }}>LOADING…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '28px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em' }}>{unreadOnly ? 'ALL CAUGHT UP.' : 'INBOX IS EMPTY.'}</span>
            <span style={{ fontSize: 11, color: T.grey500 }}>Go outside. Someone will open a portal.</span>
          </div>
        ) : rows.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => handleNotificationClick(n)}
            className="pr-drop-row"
            style={{ width: '100%', display: 'grid', gridTemplateColumns: '48px minmax(0,1fr) auto', gap: 12, alignItems: 'center', padding: '8px 10px 8px 8px', border: 0, borderRadius: RADIUS.row, background: T.white, textAlign: 'left', fontFamily: 'inherit', color: T.ink, cursor: 'pointer', transition: 'background-color .2s' }}
          >
            <span style={{ display: 'flex', padding: '0 3px 3px 0' }}>
              <NotificationIcon type={n.type} family={n.t.family} size={44} face={n.face} live={n.live} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: n.unread ? 700 : 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 }}>
                {n.secure && <LockIcon size={12} style={{ flexShrink: 0 }} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.title}</span>
              </span>
              {n.preview && (
                <span style={{ fontSize: 12, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', ...previewStyle(n) }}>{n.preview}</span>
              )}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400, whiteSpace: 'nowrap' }}>{n.ago}</span>
              {n.unread && <span aria-label="Unread" style={{ width: 8, height: 8, borderRadius: '50%', background: T.live }} />}
            </span>
          </button>
        ))}
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        <a href="/notifications" onClick={handleViewAll} className="pr-drop-viewall" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 46, borderRadius: RADIUS.pill, background: T.ink, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textDecoration: 'none', transition: 'opacity .2s' }}>
          VIEW ALL NOTIFICATIONS
        </a>
      </div>

      {/* Patron gift card — opens when a guest taps a PartyPatronGiftReceived notification. */}
      <PatronGiftCard
        isOpen={Boolean(giftCard)}
        onClose={closeGiftCard}
        brandName={giftCard?.brandName}
        brandLogoUrl={giftCard?.brandLogoUrl}
        message={giftCard?.message}
        giftAmountCents={giftCard?.giftAmountCents || 0}
        promoLink={giftCard?.promoLink}
        promoCode={giftCard?.promoCode}
        onBrowseEvents={() => {
          closeGiftCard();
          onClose();
          navigate('/featured');
        }}
      />
    </div>
  );
}

const DROP_CSS = `
  .pr-drop-row:hover { background: #F5F5F5 !important; }
  .pr-drop-markall:hover { color: #000 !important; }
  .pr-drop-viewall:hover { opacity: .8; color: #fff; }
  .pr-noscroll::-webkit-scrollbar { display: none; }
`;
